import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import createShipTo from "@insite/client-framework/Store/Pages/Addresses/Handlers/CreateShipTo";
import resetBillingAddressFormValidation from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/ResetBillingAddressFormValidation";
import resetShippingAddressFormValidation from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/ResetShippingAddressFormValidation";
import updateBillTo from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateBillTo";
import updateShipTo from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateShipTo";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import { BillToModel, ShipToModel, ValidatedAddressModel, WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import BillingAddress, { BillingAddressStyles } from "@insite/content-library/Widgets/CheckoutShipping/BillingAddress";
import PickUpAddress, { PickUpAddressStyles } from "@insite/content-library/Widgets/CheckoutShipping/PickUpAddress";
import ShippingAddress, {
    ShippingAddressStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/ShippingAddress";
import Button from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import XCircle from "@insite/mobius/Icons/XCircle";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import { isMobileDevice } from "@MasterBluePrint/Overrides/Common/Utilities/isMobileDevice";
import clearPiyoviValidationResult from "@MasterBluePrint/Store/Components/CustomCheckoutShipping/Handlers/ClearPiyoviValidationResult";
import setByPassPiyoviAddressValidation from "@MasterBluePrint/Store/Components/CustomCheckoutShipping/Handlers/SetByPassPiyoviAddressValidation";
import setBypassPiyoviByType from "@MasterBluePrint/Store/Components/CustomCheckoutShipping/Handlers/SetBypassPiyoviByType";
import setKeptUnverifiedAddress from "@MasterBluePrint/Store/Components/CustomCheckoutShipping/Handlers/SetKeptUnverifiedAddress";
import { piyoviSettingSelector } from "@MasterBluePrint/Store/Context/ContextSelector";
import { getLaunchDarklySettings } from "@MasterBluePrint/Store/Context/ContextSelector";
import { CustomApplicationState } from "@MasterBluePrint/Store/CustomApplicationState";
import themeColors, { themeFontSizes, themeFontWeights } from "@MasterBluePrint/Styles/variables";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const shipTosDataView = cart
        ? getShipTosDataView(state, { billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] })
        : undefined;
    let newAddress;
    let oneTimeAddress;
    let isShipToSameAsBillTo = false;
    let canUseBillToAsShipTo = false;
    if (cart && cart.cartLines && shipTosDataView && shipTosDataView.value) {
        const shipTos = shipTosDataView.value;
        newAddress = shipTos!.find(s => s.isNew && !s.oneTimeAddress);
        oneTimeAddress = shipTos!.find(s => s.oneTimeAddress);
        isShipToSameAsBillTo = cart.billToId === cart.shipToId;
        canUseBillToAsShipTo = shipTos!.some(shipTo => shipTo.id === cart.billToId);
    }
    const launchDarklySettings = getLaunchDarklySettings(state);
    return {
        cart,
        newAddress,
        oneTimeAddress,
        shipToState: getShipToState(state, cart ? cart.shipToId : undefined),
        shipTosDataView,
        isShipToSameAsBillTo,
        session: state.context.session,
        countries: getCurrentCountries(state),
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
        billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
        accountSettings: getSettingsCollection(state).accountSettings,
        canUseBillToAsShipTo,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        useBillingAddress: state.pages.checkoutShipping.useBillingAddress,
        billToState: getBillToState(state, cart?.billToId),
        cartPageLink: getPageLinkByPageType(state, "CartPage")?.url,
        billingAddressFormErrors: state.pages.checkoutShipping.billingAddressFormState?.formErrors,
        shippingAddressFormErrors: state.pages.checkoutShipping.shippingAddressFormState?.formErrors,
        shippingAddressFormAddress: state.pages.checkoutShipping.shippingAddressFormState?.address,
        billingAddressFormAddress: state.pages.checkoutShipping.billingAddressFormState?.address,
        isBypassingPiyoviAddressValidation: state.customCheckoutShippingState.bypassPiyoviAddressValidation,
        piyoviValidation: state.customCheckoutShippingState.piyoviValidation,
        enablePiyoviAvs: piyoviSettingSelector(state)?.enablePiyoviAvs,
        launchDarklySettings,
    };
};

const mapDispatchToProps = {
    updateShipTo,
    setCurrentShipTo,
    updatePickUpWarehouse: makeHandlerChainAwaitable(updatePickUpWarehouse),
    loadShipTo,
    loadShipTos,
    updateBillTo,
    createShipTo,
    setByPassPiyoviAddressValidation,
    setBypassPiyoviByType,
    clearPiyoviValidationResult,
    setKeptUnverifiedAddress,
    resetShippingAddressFormValidation,
    resetBillingAddressFormValidation,
    setShippingAddress: (address: ShipToModel) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({ type: "Pages/CheckoutShipping/SetShippingAddress", address });
    },
    setBillingAddress: (address: BillToModel) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({ type: "Pages/CheckoutShipping/SetBillingAddress", address });
    },
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CheckoutShippingAddressesStyles {
    loadingWrapper?: InjectableCss;
    container?: GridContainerProps;
    addressItem?: GridItemProps;
    addressContainer?: GridContainerProps;
    shippingAddressGridItem?: GridItemProps;
    shippingAddress?: ShippingAddressStyles;
    billingAddressGridItem?: GridItemProps;
    billingAddress?: BillingAddressStyles;
    pickUpAddress?: PickUpAddressStyles;
    billingAddressStyle?: InjectableCss;
    addressFormErrorsModal?: ModalPresentationProps;
    addressFormErrorsContainer?: InjectableCss;
    addressConfirmSecondaryButton?: InjectableCss;
    addressConfirmPrimaryButton?: InjectableCss;
}

export const checkoutShippingAddressesStyles: CheckoutShippingAddressesStyles = {
    loadingWrapper: {
        css: css`
            display: flex;
            height: 28.125rem;
            justify-content: center;
            align-items: center;
        `,
    },
    addressContainer: {
        css: css`
            @media screen and (min-width: 992px) and (max-width: 1199px) {
                &.both {
                    > div {
                        > div.billing-wrapper {
                            border-right: 0.0625rem solid ${themeColors.colorBorderBrands};
                            > div[data-test-selector="checkoutShipping_billingAddress"] {
                                > div {
                                    > div:last-child {
                                        > div[data-test-selector="customerAddressFormFields"] {
                                            margin-top: 7.188rem;
                                        }
                                    }
                                    
                                }
                            }
                        }
                    }
                }
                &.canUseBillToAsShipTo,
                &.oneTimeAddress {
                    > div {
                        > div.billing-wrapper {
                            border-right: 0.0625rem solid ${themeColors.colorBorderBrands};
                            > div[data-test-selector="checkoutShipping_billingAddress"] {
                                > div {
                                    > div:last-child {
                                        > div[data-test-selector="customerAddressFormFields"] {
                                            margin-top: 4rem;
                                        }
                                    }
                                    
                                }
                            }
                        }
                    }
                }
            }
            @media screen and (min-width: 1200px) {
                &.both {
                    > div {
                        > div.billing-wrapper {
                            border-right: 0.0625rem solid ${themeColors.colorBorderBrands};
                            > div[data-test-selector="checkoutShipping_billingAddress"] {
                                > div {
                                    > div:last-child {
                                        > div[data-test-selector="customerAddressFormFields"] {
                                            margin-top: 6.063rem;
                                        }
                                    }
                                    
                                }
                            }
                        }
                    }
                }
                &.canUseBillToAsShipTo,
                &.oneTimeAddress {
                    > div {
                        > div.billing-wrapper {
                            border-right: 0.0625rem solid ${themeColors.colorBorderBrands};
                            > div[data-test-selector="checkoutShipping_billingAddress"] {
                                > div {
                                    > div:last-child {
                                        > div[data-test-selector="customerAddressFormFields"] {
                                            margin-top: 4rem;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
    addressItem: { width: 12 },
    shippingAddressGridItem: { width: [12, 12, 12, 6, 6] },
    billingAddressGridItem: { width: [12, 12, 12, 6, 6] },
    addressFormErrorsModal: {
        sizeVariant: "small",
    },
    addressFormErrorsContainer: {
        css: css`
        margin-left:20px;
        .closeAddressFormErrorsContainer{
           position: absolute;
           right: 1.25rem;
           top: 24px;
           .close-label {
                color: #535E64;
                font-family: Roboto;
                font-size: .875rem;
                letter-spacing: 0.16px;
                line-height: 1;
                display: inline-block;
                margin-right: .625rem;
                vertical-align: middle;
           }
           .icon-close {
                display: inline-block;
                cursor: pointer;
                vertical-align: middle;
                line-height: 1;
                width: 1.125rem;
                height: 1.125rem;
                color: #535E64;
                margin-top:-1px;
                svg {
                    height: 1.125rem;
                    width: 1.125rem;
                }
            }
        }
        .addressFormErrorsModalHeading {
            margin-top: 2.1875rem;
            margin-bottom: 0;
            margin-left: 0;
            color: ${themeColors.colorPrimary};
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size20};
            font-weight: ${themeFontWeights.fontWeightMedium};
            line-height: 1.5625rem;
            position: relative;
            letter-spacing: 0;  

            &:after {
                height: 0.188rem;
                width: 2.50rem;
                background-color:  ${themeColors.colorRed};
                content: "";
                display: inline-block;
                position: absolute;
                bottom: -0.4rem;
                left: 0.0625rem;
            }
        }
        .addressFormErrorMessageParentDiv{
            width:"100%";
            .addressFormErrorMessageChild1Div{
                float: left;
                width: 8%;
                .icon-warning{
                    color: ${themeColors.colorDanger};
                }
            }
            .addressFormErrorMessageChild2Div{
                width: 92%;
                .addressFormErrorMessage{
                    margin-top: 2.1875rem;
                    margin-bottom: 0;
                    color: ${themeColors.colorDanger};
                    font-family: 'Roboto', sans-serif;
                    font-size: ${themeFontSizes.size14};
                    font-weight: ${themeFontWeights.fontWeightBold};
                    line-height: 1.5625rem;
                    position: relative;
                    margin-left: 0;
                }        
                .addressFormErrorHelperText{
                    margin-bottom: 1.5625rem;
                    color: ${themeColors.colorDanger};
                    font-family: 'Roboto', sans-serif;
                    font-style: italic;
                    font-size: ${themeFontSizes.size14};
                    line-height: 1.5625rem;
                    position: relative;
                    margin-left: 0;
                }
            }
        }        
        .byPassAddressValidationDiv{
            margin-bottom: 1.5625rem;
        }
        .byPassAddressValidationText{
            color: ${themeColors.colorAddress};
            font-family: Lato;
            font-size: ${themeFontSizes.size15}; 
            letter-spacing: 0.17px;
            line-height: 18px;
        }
        .byPassAddressValidationHLine{
            width:"100%",
            background-color:"#003366";
            box-sizing: border-box;
            height: 1px; 
            border: 1px solid #D7D7D7;   
            opacity: 0.72;
            margin-bottom: 1.5625rem;
        }
        .byPassAddressValidationContinueButtonDiv{
            text-align:center;
            .byPassAddressValidationContinueButton{
                box-sizing: border-box;
                height: 40px;
                width: 222px;
                border: 1px solid ${themeColors.colorDisabled};
                background-color: ${themeColors.colorBackgroundContrast};
                border-radius: 2px;
                span{
                    color:${themeColors.colorWhite};
                    font-family: Roboto;
                    font-size: ${themeFontSizes.size15};
                    font-weight: ${themeFontWeights.fontWeightBold};
                    letter-spacing: 0;
                    line-height: 21px;
                    text-align: center;
                }
                &:disabled{
                    background-color:${themeColors.colorWhite};
                    span{
                        color:${themeColors.colorDisabled};
                        font-family: Roboto;
                        font-size: ${themeFontSizes.size15};
                        font-weight: ${themeFontWeights.fontWeightBold};
                        letter-spacing: 0;
                        line-height: 21px;
                        text-align: center;
                    }
                }
            }
        }
        .piyoviModalBody{
            margin: 1.25rem 0;
            color: ${themeColors.colorAddress};
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size14};
            line-height: 1.25rem;
        }
        .piyoviOptionLabel{
            display: block;
            font-weight: ${themeFontWeights.fontWeightBold};
            color: ${themeColors.colorPrimary};
        }
        .piyoviOptionAddress{
            color: ${themeColors.colorAddress};
            font-family: 'Roboto', sans-serif;
            font-size: ${themeFontSizes.size14};
            line-height: 1.25rem;
        }
        .piyoviOptionAddressBlock{
            margin-bottom: 1.25rem;
            .piyoviOptionAddress{
                margin-left: 0;
            }
        }
        .piyoviModalActions{
            display: flex;
            justify-content: space-between;
            gap: 0.9375rem;
            margin-top: 1rem;
        }
        `,
    },
    addressConfirmSecondaryButton: {
        css: css`
            text-transform: capitalize;
            text-decoration: underline;
            flex: 1;
            cursor: pointer;
            height: 44px;
            line-height: 1;
            transition: all 200ms ease-in-out;
            border-radius: 0.125rem;
            padding: 0 21px;
            border: 2px solid #002554;
            background: #fff;
            color: #002554;
            font-family: Roboto;
            font-size: 0.875rem;
            text-align: center;
            span {
                height: 44px;
                line-height: 44px;
                color: #002554 !important;
                vertical-align: baseline;
            }
            &:hover,
            &:active,
            &:focus {
                border-color: #002554;
                border-radius: 0.125rem;
                background-color: #2060ad;
                color: #fff !important;
                span {
                    color: #fff !important;
                }
            }
        `,
    },
    addressConfirmPrimaryButton: {
        css: css`
            text-transform: uppercase;
            height: 44px;
            font-size: 0.875rem;
            flex: 1;
            text-align: center;
            span {
                font-size: 0.875rem;
            }
        `,
    },
};

export const fulfillmentMethodAndAddressStyles = checkoutShippingAddressesStyles;
const styles = checkoutShippingAddressesStyles;

const CheckoutShippingAddresses: FC<Props> = ({
    cart,
    shipToState,
    shipTosDataView,
    session,
    countries,
    shipToAddressFields,
    billToAddressFields,
    setCurrentShipTo,
    updatePickUpWarehouse,
    loadShipTo,
    loadShipTos,
    updateBillTo,
    billToState,
    updateShipTo,
    canUseBillToAsShipTo,
    isShipToSameAsBillTo,
    newAddress,
    oneTimeAddress,
    children,
    cartPageLink,
    history,
    createShipTo,
    billingAddressFormErrors,
    shippingAddressFormErrors,
    shippingAddressFormAddress,
    billingAddressFormAddress,
    enablePiyoviAvs,
    piyoviValidation,
    setByPassPiyoviAddressValidation,
    setBypassPiyoviByType,
    clearPiyoviValidationResult,
    setKeptUnverifiedAddress,
    resetShippingAddressFormValidation,
    resetBillingAddressFormValidation,
    setShippingAddress,
    setBillingAddress,
    currentUserIsGuest,
    useBillingAddress,
}) => {
    let featureFlags: string[] = ["TEUS-7942"];
    const flagValues = useLaunchDarklyFlags({ featureFlags });
    const enableVerticalAddressLayoutCheckout = flagValues["TEUS-7942"];

    const getGridItemWidth = () => {
        return (isMobileDevice() || !enableVerticalAddressLayoutCheckout) ? [12, 12, 12, 6, 6] : [12, 12, 12, 12, 12];
    }

    const dynamicStyles = {
        ...checkoutShippingAddressesStyles,
        shippingAddressGridItem: {
            width: getGridItemWidth() as unknown as GridWidths,
        },
        billingAddressGridItem: {
            width: getGridItemWidth() as unknown as GridWidths,
        },
        addressContainer: {
            css: css`
                @media screen and (min-width: 992px) and (max-width: 1199px) {
                    &.both {
                        > div {
                            > div.billing-wrapper {
                                border-right: ${(isMobileDevice() || !enableVerticalAddressLayoutCheckout) ? `0.0625rem solid ${themeColors.colorBorderBrands}` : 'none'};
                                > div[data-test-selector="checkoutShipping_billingAddress"] {
                                    > div {
                                        > div:last-child {
                                            > div[data-test-selector="customerAddressFormFields"] {
                                                margin-top: 7.188rem;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    &.canUseBillToAsShipTo,
                    &.oneTimeAddress {
                        > div {
                            > div.billing-wrapper {
                                border-right: ${(isMobileDevice() || !enableVerticalAddressLayoutCheckout) ? `0.0625rem solid ${themeColors.colorBorderBrands}` : 'none'};
                                > div[data-test-selector="checkoutShipping_billingAddress"] {
                                    > div {
                                        > div:last-child {
                                            > div[data-test-selector="customerAddressFormFields"] {
                                                margin-top: 4rem;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                @media screen and (min-width: 1200px) {
                    &.both {
                        > div {
                            > div.billing-wrapper {
                                border-right: ${(isMobileDevice() || !enableVerticalAddressLayoutCheckout) ? `0.0625rem solid ${themeColors.colorBorderBrands}` : 'none'};
                                > div[data-test-selector="checkoutShipping_billingAddress"] {
                                    > div {
                                        > div:last-child {
                                            > div[data-test-selector="customerAddressFormFields"] {
                                                margin-top: 6.063rem;
                                                margin-top: ${(isMobileDevice() || !enableVerticalAddressLayoutCheckout) ? '6.063rem' : '0rem'};
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    &.canUseBillToAsShipTo,
                    &.oneTimeAddress {
                        > div {
                            > div.billing-wrapper {
                                border-right: ${(isMobileDevice() || !enableVerticalAddressLayoutCheckout) ? `0.0625rem solid ${themeColors.colorBorderBrands}` : 'none'};
                                > div[data-test-selector="checkoutShipping_billingAddress"] {
                                    > div {
                                        > div:last-child {
                                            > div[data-test-selector="customerAddressFormFields"] {
                                                margin-top: 4rem;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `,
        },
    };

    const toasterContext = React.useContext(ToasterContext);

    useEffect(() => {
        if (cart && shipTosDataView && !shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos({ billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] });
        }
    });

    useEffect(() => {
        if (!shipToState.value && cart) {
            loadShipTo({ billToId: cart.billToId!, shipToId: cart.shipToId! });
        }
    });

    if (!cart) {
        return (
            <StyledWrapper {...dynamicStyles.loadingWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!countries || !shipToState.value || !billToState.value) {
        return null;
    }

    const billingCountries = currentUserIsGuest && isShipToSameAsBillTo && useBillingAddress
        ? countries?.filter(country => country.abbreviation === "US")
        : countries;

    const { billToId } = cart;
    const { fulfillmentMethod } = session;
    const piyoviAddressMessage = siteMessage("Piyovi_Address_Message").toString();
    const piyoviAddressMessageHelpText = siteMessage("Piyovi_Address_Message_Help_Text").toString();

    const changeShippingAddressHandler = (address: ShipToModel) => {
        if (address.isNew) {
            createShipTo({
                billToId: billToId!,
                shipTo: {
                    ...address,
                },
                onComplete: result => {
                    if (result.apiResult && result.apiParameter?.shipTo.isDefault) {
                        setCurrentShipTo({ shipToId: result.apiResult.id });
                    }
                },
            });
        }
        else {
            updateShipTo({
                billToId: billToId!,
                shipTo: { ...address, },
                isEdit: true,
                onSuccess: hasCartlines => {
                    if (!hasCartlines) {
                        history.push(cartPageLink!);
                    }
                },
            });
        }
    };

    const changeBillingAddressHandler = (address: BillToModel) => {
        updateBillTo({
            billTo: address,
            isEdit: true,
            onSuccess: hasCartlines => {
                if (!hasCartlines) {
                    history.push(cartPageLink!);
                }
            },
        });
    };

    // ---- Piyovi address confirmation modal ----
    const isPiyoviModalOpen = !!piyoviValidation && !!enablePiyoviAvs;
    const hasSuggestion = !!piyoviValidation?.hasSuggestion && !!piyoviValidation?.suggestedAddress;
    const activeAddressType =
        piyoviValidation?.addressType ??
        (shippingAddressFormErrors?.isPiyoviValidationError
            ? "shipping"
            : billingAddressFormErrors?.isPiyoviValidationError
                ? "billing"
                : "shipping");

    const formatAddressLines = (address?: ValidatedAddressModel) => {
        if (!address) {
            return [];
        }
        const line1 = [address.address1, address.address2].filter(Boolean).join(", ");
        const line2 = [
            [address.city, address.stateProvince].filter(Boolean).join(", "),
            address.zipPostalCode,
        ]
            .filter(Boolean)
            .join(" ");
        return [line1, line2].filter(Boolean);
    };

    const triggerCheckoutContinue = () => {
        document.getElementById("checkoutShippingCartTotalContinueButton")?.click();
    };

    const buildMergedAddress = <T extends ShipToModel | BillToModel>(
        formAddress: T,
        suggested: ValidatedAddressModel,
    ): T => {
        const resolvedState = suggested.stateProvince
            ? formAddress.country?.states?.find(s => s.abbreviation === suggested.stateProvince) ?? formAddress.state
            : formAddress.state;
        return {
            ...formAddress,
            address1: suggested.address1 ?? formAddress.address1,
            address2: suggested.address2 ?? formAddress.address2,
            city: suggested.city ?? formAddress.city,
            postalCode: suggested.zipPostalCode ?? formAddress.postalCode,
            state: resolvedState,
        };
    };

    // Guests bypass only the address just resolved, so the other address (e.g. one-time
    // shipping vs billing) still validates on the auto re-Continue. Non-guests keep the
    // single global bypass (today's behavior — one address per checkout).
    const applyPiyoviBypass = () => {
        if (currentUserIsGuest) {
            setBypassPiyoviByType({ addressType: activeAddressType, isBypassing: true });
        } else {
            setByPassPiyoviAddressValidation({ isBypassingPiyoviAddressValidation: true });
        }
    };

    const handleUseSuggestedAddress = () => {
        const suggested = piyoviValidation?.suggestedAddress;
        if (!suggested) {
            return;
        }
        if (activeAddressType === "billing" && billingAddressFormAddress) {
            const merged = buildMergedAddress(billingAddressFormAddress, suggested);
            setBillingAddress(merged);
            changeBillingAddressHandler(merged);
        } else if (shippingAddressFormAddress) {
            const merged = buildMergedAddress(shippingAddressFormAddress, suggested);
            setShippingAddress(merged);
            changeShippingAddressHandler(merged);
        }
        applyPiyoviBypass();
        clearPiyoviValidationResult({});
        triggerCheckoutContinue();
    };

    const handleKeepEnteredAddress = () => {
        applyPiyoviBypass();
        setKeptUnverifiedAddress({ keptUnverifiedAddress: true });
        clearPiyoviValidationResult({});
        triggerCheckoutContinue();
    };

    const handleEditAddress = () => {
        if (activeAddressType === "billing") {
            resetBillingAddressFormValidation({});
        } else {
            resetShippingAddressFormValidation({});
        }
        clearPiyoviValidationResult({});
    };

    const suggestedLines = formatAddressLines(piyoviValidation?.suggestedAddress);
    const enteredLines = formatAddressLines(piyoviValidation?.enteredAddress);

    const useBillingAddressHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setCurrentShipTo({ shipToId: cart.billToId! });
    };

    const handlePickUpAddressChange = async (pickUpWarehouse: WarehouseModel) => {
        await updatePickUpWarehouse({ pickUpWarehouse });
        toasterContext.addToast({
            body: translate("Pick Up Address Updated"),
            messageType: "success",
        });
    };

    let billingAddressAlignment = "";
    if (canUseBillToAsShipTo && oneTimeAddress) {
        billingAddressAlignment = "both"
    }
    else if (canUseBillToAsShipTo) {
        billingAddressAlignment = "canUseBillToAsShipTo";
    }
    else if (oneTimeAddress) {
        billingAddressAlignment = "oneTimeAddress";
    }

    return (
        <GridContainer {...dynamicStyles.container}>
            <GridItem {...dynamicStyles.addressItem}>
                <GridContainer {...dynamicStyles.addressContainer} className={billingAddressAlignment}>
                    {billToAddressFields && (
                        <GridItem {...dynamicStyles.billingAddressGridItem} {...dynamicStyles.billingAddressStyle} className="billing-wrapper">
                            <BillingAddress
                                address={billToState.value}
                                onChange={changeBillingAddressHandler}
                                countries={billingCountries}
                                addressFieldDisplayCollection={billToAddressFields}
                                extendedStyles={dynamicStyles.billingAddress}

                            />
                        </GridItem>
                    )}
                    {fulfillmentMethod === FulfillmentMethod.Ship && shipToAddressFields && (
                        <GridItem {...dynamicStyles.shippingAddressGridItem}>
                            <ShippingAddress
                                address={shipToState.value}
                                currentBillToId={cart.billToId}
                                countries={countries}
                                addressFieldDisplayCollection={shipToAddressFields}
                                onChange={changeShippingAddressHandler}
                                showUseBillingAddress={canUseBillToAsShipTo}
                                isUseBillingAddressDisabled={isShipToSameAsBillTo}
                                onClickUseBillingAddress={useBillingAddressHandler}
                                extendedStyles={dynamicStyles.shippingAddress}
                                newAddress={newAddress}
                                oneTimeAddress={oneTimeAddress}
                            >
                                {children}
                            </ShippingAddress>
                        </GridItem>
                    )}
                    {fulfillmentMethod === FulfillmentMethod.PickUp && session.pickUpWarehouse && (
                        <GridItem {...dynamicStyles.shippingAddressGridItem}>
                            <PickUpAddress
                                address={session.pickUpWarehouse}
                                onChange={handlePickUpAddressChange}
                                extendedStyles={dynamicStyles.pickUpAddress}
                            />
                        </GridItem>
                    )}
                    {
                        enablePiyoviAvs && <Modal
                            isOpen={isPiyoviModalOpen}
                            data-test-selector="piyoviAddressConfirmModal"
                            {...dynamicStyles.addressFormErrorsModal}
                            handleClose={() => handleEditAddress()}
                            className="confirm-remove-modal"
                        >
                            <Typography as="div" {...dynamicStyles.addressFormErrorsContainer}>
                                <Typography as="div" onClick={handleEditAddress} className="closeAddressFormErrorsContainer">
                                    <span className="close-label">{translate("Close")}</span>
                                    <Icon className="icon-close" src={XCircle} />
                                </Typography>
                                <Typography as="div">
                                    <Typography className="addressFormErrorsModalHeading" as="h1">
                                        {translate("Verify your address")}
                                    </Typography>
                                </Typography>

                                {hasSuggestion ? (
                                    <>
                                        <Typography as="p" className="piyoviModalBody">
                                            {translate(
                                                "We were unable to verify the address you entered, which may result in delivery delays. Please confirm your address below.",
                                            )}
                                        </Typography>
                                        <Typography as="div" className="piyoviOptionAddressBlock" data-test-selector="piyoviSuggestedOption">
                                            <Typography as="span" className="piyoviOptionLabel">
                                                {translate("Suggested address (verified)")}
                                            </Typography>
                                            {suggestedLines.map((line, i) => (
                                                <Typography as="div" key={`sug-${i}`} className="piyoviOptionAddress">
                                                    {line}
                                                </Typography>
                                            ))}
                                        </Typography>
                                        <Typography as="div" className="piyoviOptionAddressBlock" data-test-selector="piyoviEnteredOption">
                                            <Typography as="span" className="piyoviOptionLabel">
                                                {translate("You entered")}
                                            </Typography>
                                            {enteredLines.map((line, i) => (
                                                <Typography as="div" key={`ent-${i}`} className="piyoviOptionAddress">
                                                    {line}
                                                </Typography>
                                            ))}
                                        </Typography>
                                        <Typography as="hr" className="byPassAddressValidationHLine"></Typography>
                                        <Typography as="div" className="piyoviModalActions">
                                            <Button
                                                {...dynamicStyles.addressConfirmSecondaryButton}
                                                onClick={handleKeepEnteredAddress}
                                                data-test-selector="piyoviKeepMyAddressButton"
                                            >
                                                {translate("Keep my address")}
                                            </Button>
                                            <Button
                                                {...dynamicStyles.addressConfirmPrimaryButton}
                                                onClick={handleUseSuggestedAddress}
                                                data-test-selector="piyoviUseSuggestedButton"
                                            >
                                                {translate("Use suggested")}
                                            </Button>
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <Typography as="div" className="addressFormErrorMessageParentDiv">
                                            <Typography as="div" className="addressFormErrorMessageChild1Div">
                                                <Icon className="icon-warning" src={AlertTriangle} />
                                            </Typography>
                                            <Typography as="div" className="addressFormErrorMessageChild2Div">
                                                <Typography as="div">
                                                    <Typography className="addressFormErrorMessage" as="h3">
                                                        {piyoviAddressMessage || translate("We couldn't verify this address. It may be incomplete or incorrect and could result in delivery delays.")}
                                                    </Typography>
                                                </Typography>
                                                <Typography as="div">
                                                    <Typography className="addressFormErrorHelperText" as="p">
                                                        {piyoviAddressMessageHelpText || translate("Please review your address carefully before continuing.")}
                                                    </Typography>
                                                </Typography>
                                            </Typography>
                                        </Typography>
                                        <Typography as="div" className="piyoviOptionAddressBlock">
                                            {enteredLines.map((line, i) => (
                                                <Typography as="div" key={`ent-b-${i}`} className="piyoviOptionAddress">
                                                    {line}
                                                </Typography>
                                            ))}
                                        </Typography>
                                        <Typography as="hr" className="byPassAddressValidationHLine"></Typography>
                                        <Typography as="div" className="piyoviModalActions">
                                            <Button
                                                {...dynamicStyles.addressConfirmSecondaryButton}
                                                onClick={handleEditAddress}
                                                data-test-selector="piyoviEditAddressButton"
                                            >
                                                {translate("Edit address")}
                                            </Button>
                                            <Button
                                                {...dynamicStyles.addressConfirmPrimaryButton}
                                                onClick={handleKeepEnteredAddress}
                                                data-test-selector="piyoviContinueWithAddressButton"
                                            >
                                                {translate("Continue with this address")}
                                            </Button>
                                        </Typography>
                                    </>
                                )}
                            </Typography>
                        </Modal>
                    }
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutShippingAddresses)),
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage"],
        displayName: "Addresses",
    },
};

export default widgetModule;
